import {announce} from '@github-ui/aria-live'
import type {
  OnboardingPrompt,
  OnboardingPromptFeatureRequest,
  OnboardingPromptIcon,
  OnboardingPrompts as OnboardingPromptsPayload,
} from '@github-ui/code-view-types'
import {
  IpmErrorBoundary,
  type IpmMetricTags,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {CodespacesIcon, CopilotIcon, PeopleIcon, PersonAddIcon} from '@primer/octicons-react'
import {Button, Link, LinkButton, Stack} from '@primer/react'
import {Card} from '@primer/react/experimental'
import {Profiler, useEffect, useMemo, useState} from 'react'

import styles from './OnboardingPrompts.module.css'

export interface OnboardingPromptsProps {
  prompts?: OnboardingPromptsPayload
}

/**
 * Top-of-page two-up grid shown to repo admins on an empty repo.
 *
 * All variant selection (Copilot eligibility, Codespaces flag, org/user invite
 * CTAs, MFR state, etc.) is owned by the backend and arrives pre-resolved on
 * `LayoutPayload.repoState.data.onboardingPrompts`. This component renders the
 * backend-provided prompts and owns the interactive request/cancel behavior for
 * prompt actions.
 */
export function OnboardingPrompts({prompts}: OnboardingPromptsProps) {
  if (!prompts) return null
  const {primary, secondary} = prompts
  if (!primary && !secondary) return null

  return (
    <Stack
      direction={{narrow: 'vertical', regular: 'horizontal'}}
      gap="normal"
      align="stretch"
      className={styles.PromptsGrid}
    >
      {primary && (
        <Stack.Item grow className={styles.PromptItem}>
          <PromptCard prompt={primary} />
        </Stack.Item>
      )}
      {secondary && (
        <Stack.Item grow className={styles.PromptItem}>
          <PromptCard prompt={secondary} />
        </Stack.Item>
      )}
    </Stack>
  )
}

const ICONS: Record<OnboardingPromptIcon, Icon> = {
  copilot: CopilotIcon,
  codespaces: CodespacesIcon,
  people: PeopleIcon,
  'person-add': PersonAddIcon,
}

function PromptCard({prompt}: {prompt: OnboardingPrompt}) {
  return (
    <Card className={styles.Prompt}>
      <Card.Icon icon={ICONS[prompt.icon]} />
      <Card.Heading>{prompt.title}</Card.Heading>
      <Card.Description>{prompt.description}</Card.Description>
      <Card.Metadata>
        {prompt.variant === 'copilot-org-member' && prompt.featureRequest ? (
          <FeatureRequestButton prompt={prompt} featureRequest={prompt.featureRequest} />
        ) : (
          <LinkButton
            href={prompt.href}
            {...(prompt.analytics && {'data-analytics-event': JSON.stringify(prompt.analytics)})}
          >
            {prompt.buttonLabel}
          </LinkButton>
        )}
      </Card.Metadata>
    </Card>
  )
}

type RequestState = 'prompt' | 'requested' | 'dismissed'

function initialState(featureRequest: OnboardingPromptFeatureRequest): RequestState {
  if (featureRequest.dismissed) return 'dismissed'
  if (featureRequest.requested) return 'requested'
  return 'prompt'
}

function FeatureRequestButton({
  prompt,
  featureRequest,
}: {
  prompt: OnboardingPrompt
  featureRequest: OnboardingPromptFeatureRequest
}) {
  const [state, setState] = useState<RequestState>(() => initialState(featureRequest))
  const [pending, setPending] = useState(false)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'code-view',
      component: 'OnboardingPromptFeatureRequest',
      surface: 'feature_request_widget',
      // eslint-disable-next-line camelcase
      display_location: 'empty_repo',
      category: prompt.analytics?.category ?? 'member_requests',
      // eslint-disable-next-line camelcase
      nudge_id: 'member_requests_feature_request',
      variant: prompt.variant,
    }),
    [prompt.analytics?.category, prompt.variant],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const submit = async () => {
    if (pending) return
    reportIpmAction('submit', telemetryTags)
    setPending(true)
    const previous = state
    setState('requested')
    try {
      const body = new URLSearchParams({feature: featureRequest.feature})
      const res = await verifiedFetch(featureRequest.actionPath, {method: 'POST', body})
      if (res.ok) {
        announce(`Request for ${featureRequest.featureName} submitted successfully`)
      } else {
        setState(previous)
        announce(`Request for ${featureRequest.featureName} failed, please try again`)
      }
    } catch {
      setState(previous)
      announce(`Request for ${featureRequest.featureName} failed, please try again`)
    } finally {
      setPending(false)
    }
  }

  const cancel = async () => {
    if (pending) return
    reportIpmDismiss(telemetryTags)
    setPending(true)
    const previous = state
    setState('prompt')
    try {
      const body = new URLSearchParams({feature: featureRequest.feature})
      const res = await verifiedFetch(featureRequest.actionPath, {method: 'DELETE', body})
      if (res.ok) {
        announce(`Request for ${featureRequest.featureName} removed`)
      } else {
        setState(previous)
        announce(`Removing request for ${featureRequest.featureName} failed, please try again`)
      }
    } catch {
      setState(previous)
      announce(`Removing request for ${featureRequest.featureName} failed, please try again`)
    } finally {
      setPending(false)
    }
  }

  const requested = state === 'requested'

  const content =
    state === 'dismissed' ? (
      <Stack gap="condensed">
        <span>
          Your request for {featureRequest.featureName} was declined by an admin
          {featureRequest.dismissedAt ? ` on ${featureRequest.dismissedAt}` : ''}.
        </span>
        <span>
          <Link inline href={`/orgs/${featureRequest.organization}/people?query=role%3Aowner`}>
            Contact an admin
          </Link>{' '}
          for more details around their decision.
        </span>
      </Stack>
    ) : (
      <Stack direction="horizontal" gap="normal" align="center">
        <Button
          onClick={requested ? cancel : submit}
          disabled={pending}
          {...(!requested && prompt.analytics && {'data-analytics-event': JSON.stringify(prompt.analytics)})}
        >
          {requested ? 'Remove request' : prompt.buttonLabel}
        </Button>
        {requested && (
          <span className="color-fg-muted">
            You&apos;ve successfully submitted a request to your organization&apos;s admins for access to{' '}
            {featureRequest.featureName}. An admin will need to approve your request before you can use this feature.
          </span>
        )}
      </Stack>
    )

  const timed = renderTiming.isEnabled ? (
    <Profiler id="OnboardingPromptFeatureRequest" onRender={renderTiming.onRender}>
      {content}
    </Profiler>
  ) : (
    content
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="OnboardingPromptFeatureRequest">
      {timed}
    </IpmErrorBoundary>
  )
}
